import { Entity } from '@core/entities/entity'
import { ILinkedProcesses } from './ILinkedProcesses'
import { plainToInstance, Type } from 'class-transformer'
import { LinkedProcessItemResult } from './LinkedProcessItemResult/LinkedProcessItemResult'
import { LinkedRegisterItemResult } from './LinkedRegisterItemResult'
import moment from 'moment'

export class LinkedProcesses extends Entity<ILinkedProcesses> {
  @Type(() => LinkedProcessItemResult)
  linkedProcessItemResults: Array<LinkedProcessItemResult>

  public getLinkedPermits(): Array<LinkedProcessItemResult> {
    return (
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedPermitResult,
      ) || []
    )
  }

  public getLinkedObservations(): Array<LinkedProcessItemResult> {
    return (
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedObservationResult,
      ) || []
    )
  }

  public getLinkedChecklists(): Array<LinkedProcessItemResult> {
    return (
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedChecklistResult,
      ) || []
    )
  }

  public getLinkedInspections(): Array<LinkedProcessItemResult> {
    return (
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedInspectionResult,
      ) || []
    )
  }

  public getLinkedTasks(options?: {
    overDueFirst?: boolean
  }): Array<LinkedProcessItemResult> {
    const tasks =
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedTaskResult,
      ) || []

    if (options?.overDueFirst) {
      return [
        // sort over due tasks by due date, oldest first
        ...tasks
          .filter((task) => task.linkedTaskResult.isOverdue())
          .sort((a, b) => {
            return moment(a.linkedTaskResult.dueDate).diff(
              moment(b.linkedTaskResult.dueDate),
            )
          }),
        // sort ramaining tasks by due date, newest first
        ...tasks
          .filter((task) => !task.linkedTaskResult.isOverdue())
          .sort((a, b) => {
            return moment(b.linkedTaskResult.dueDate).diff(
              moment(a.linkedTaskResult.dueDate),
            )
          }),
      ]
    }

    return tasks
  }

  public getLinkedRegisterItems(): Array<LinkedProcessItemResult> {
    return (
      this.linkedProcessItemResults.filter(
        (process) => !!process.linkedRegisterItemResult,
      ) || []
    )
  }

  public getLinkedProcessItemsByRegister(): Array<{
    id: number
    name: string
    linkedProcessItemResults: Array<LinkedProcessItemResult>
  }> {
    const groupedByRegister = this.getLinkedRegisterItems().reduce(
      (acc, process) => {
        const registerId = process.linkedRegisterItemResult.register.id
        if (!acc[registerId]) {
          acc[registerId] = {
            id: registerId,
            name: process.linkedRegisterItemResult.register.name,
            linkedProcessItemResults: [],
          }
        }
        acc[registerId].linkedProcessItemResults.push(process)

        return acc
      },
      {} as Record<
        number,
        {
          id: number
          name: string
          linkedProcessItemResults: Array<LinkedProcessItemResult>
        }
      >,
    )

    return Object.values(groupedByRegister)
  }

  public getLinkedRegisterItemsByRegister(): Array<{
    id: number
    name: string
    linkedRegisterItemResults: Array<LinkedRegisterItemResult>
  }> {
    return this.getLinkedProcessItemsByRegister()?.map((register) => ({
      ...register,
      linkedRegisterItemResults: register.linkedProcessItemResults?.map(
        (item) =>
          LinkedRegisterItemResult.new({
            ...item?.linkedRegisterItemResult,
            previewPropertyIds: item?.getPreviewPropertyIds(),
          }),
      ),
    }))
  }

  public static new(payload: unknown): LinkedProcesses {
    const entity = plainToInstance(LinkedProcesses, payload)

    return entity
  }
}
